import clsx from "clsx"
import { ComponentPropsWithoutRef, createContext, useCallback, useContext, useEffect, useRef, useState } from "react"
import { Drawer } from "vaul"

import useControllableOpenState from "@hooks/useControllableOpenState"
import useDrawerDepth from "@hooks/useDrawerDepth"
import useHeightCSSVariable from "@hooks/useHeightCSSVariable"
import useLayerStack, { LAYER_Z_INDEX } from "@hooks/useLayerStack"

import Overlay from "@particles/Overlay/Overlay"

import BottomDrawer from "./BottomDrawer"
import styles from "./BottomDrawer.module.scss"
import { BottomDrawerContextValues, BottomDrawerProps } from "./BottomDrawer.types"
import DrawerDragIndicator from "./DrawerDragIndicator/DrawerDragIndicator"

export const BottomDrawerContext = createContext<BottomDrawerContextValues>({
    isDismissable: true,
    hasDragIndicator: true,
    isOpen: false,
    setIsOpen: () => {},
    isFirstDrawer: true,
    snapBackSignal: 0,
    overlayZIndex: LAYER_Z_INDEX.BASE + LAYER_Z_INDEX.OVERLAY_OFFSET,
    surfaceZIndex: LAYER_Z_INDEX.BASE + LAYER_Z_INDEX.SURFACE_OFFSET,
})
BottomDrawerContext.displayName = "BottomDrawer"

const DRAWER_EXIT_ANIMATION_MS = 500

function Root(props: BottomDrawerProps) {
    const {
        isOpen: controlledOpen,
        onOpenChange,
        isDismissable = true,
        hasDragIndicator = isDismissable,
        children,
    } = props

    const [snapBackSignal, setSnapBackSignal] = useState(0)
    const { drawerDepth, incrementDepth, decrementDepth } = useDrawerDepth()
    const { isControlled, isOpen, setIsOpen } = useControllableOpenState({
        isOpen: controlledOpen,
        onOpenChange,
    })

    // closeDelay keeps the layer registered during the slide-out animation so
    // z-index doesn't collapse before the drawer finishes closing.
    const { isStackActive, overlayZIndex, surfaceZIndex } = useLayerStack(isOpen, {
        closeDelay: DRAWER_EXIT_ANIMATION_MS,
    })

    // Track whether this drawer was the first one when it opened
    // We use a ref to store the value so it persists across re-renders
    const isFirstDrawerRef = useRef<boolean | null>(null)
    const hasRegisteredDepthRef = useRef(false)

    // Synchronously determine if this is the first drawer when it opens
    // This must happen during render (not in an effect) so the context value is correct immediately
    if (isOpen && isFirstDrawerRef.current === null) {
        // First time this drawer is opening - capture whether we're the first drawer
        isFirstDrawerRef.current = drawerDepth === 0
    } else if (!isStackActive && isFirstDrawerRef.current !== null) {
        // Drawer is closing - reset for next open
        isFirstDrawerRef.current = null
    }

    // Keep the drawer registered in the nested depth stack until the close animation completes.
    useEffect(() => {
        if (isStackActive && !hasRegisteredDepthRef.current) {
            hasRegisteredDepthRef.current = true
            incrementDepth()
        } else if (!isStackActive && hasRegisteredDepthRef.current) {
            hasRegisteredDepthRef.current = false
            decrementDepth()
        }
    }, [isStackActive, incrementDepth, decrementDepth])

    // In controlled mode, delegate state changes to the parent via onOpenChange
    // rather than letting Radix mutate internal state directly. This ensures the
    // parent is the source of truth and prevents desync when Radix fires dismissals
    // that the parent wants to ignore (e.g., when a confirmation prompt opens above).
    const handleOpenChange = useCallback(
        (newOpen: boolean) => {
            setIsOpen(newOpen)

            if (isControlled) {
                if (!newOpen) {
                    // Signal Content to snap back if the parent refuses the close.
                    // The effect in Content checks data-state after React processes
                    // the parent's response — if still "open", it resets the transform.
                    setSnapBackSignal((prev) => prev + 1)
                }
            }
        },
        [isControlled, setIsOpen],
    )

    // Default to false if not yet determined (drawer not open)
    const isFirstDrawer = isFirstDrawerRef.current ?? false

    return (
        <Drawer.Root
            open={isOpen}
            onOpenChange={handleOpenChange}
            shouldScaleBackground={true}
            dismissible={isDismissable}
        >
            <BottomDrawerContext.Provider
                value={{
                    isDismissable,
                    hasDragIndicator,
                    setIsOpen: handleOpenChange,
                    isOpen,
                    isFirstDrawer,
                    snapBackSignal,
                    overlayZIndex,
                    surfaceZIndex,
                }}
            >
                {children}
            </BottomDrawerContext.Provider>
        </Drawer.Root>
    )
}

function Trigger(props: ComponentPropsWithoutRef<typeof Drawer.Trigger>) {
    const { children, ...rest } = props

    return <Drawer.Trigger {...rest}>{children}</Drawer.Trigger>
}

function Content(props: ComponentPropsWithoutRef<typeof Drawer.Content>) {
    const { children, className, style, ...rest } = props
    const { hasDragIndicator, isOpen, isFirstDrawer, snapBackSignal, overlayZIndex, surfaceZIndex } =
        useContext(BottomDrawerContext)

    // Only set the CSS variable for the first (non-nested) drawer
    // This prevents nested drawers from overwriting the parent drawer's height variable
    const shouldSetCSSVariable = isOpen && isFirstDrawer
    const contentRef = useHeightCSSVariable<HTMLDivElement>("--drawer-content-height", shouldSetCSSVariable)

    // When a controlled close is delegated to the parent but the parent refuses (keeps open=true),
    // Vaul leaves inline drag transforms on the content element. This effect detects that case
    // (isOpen is still true) and smoothly snaps the drawer back to its open position.
    //
    // Why we need a forced reflow: Vaul sets `transition: none` inline during drag to prevent
    // animation while tracking the finger. cancelDrag() doesn't clear it. Without a reflow
    // between restoring the transition and resetting the transform, the browser batches both
    // changes and the snap-back would be instant (no smooth animation).
    useEffect(() => {
        if (snapBackSignal === 0 || !isOpen) {
            return
        }

        const el = contentRef.current
        if (!el) {
            return
        }

        const transition = "0.5s cubic-bezier(0.32, 0.72, 0, 1)"

        el.style.transition = `transform ${transition}`
        el.getBoundingClientRect() // Force reflow so the browser registers the transition before the transform change
        el.style.transform = "translate3d(0, 0, 0)"

        // Also restore overlay opacity (Vaul fades it during drag-to-dismiss)
        const overlay = el.parentElement?.querySelector("[data-vaul-overlay]") as HTMLElement | null
        if (overlay) {
            overlay.style.transition = `opacity ${transition}`
            overlay.style.opacity = "1"
        }
    }, [snapBackSignal])

    return (
        <Drawer.Portal>
            <Overlay isActive={isOpen} style={{ zIndex: overlayZIndex }} />
            <Drawer.Content
                ref={contentRef}
                className={clsx(styles.content, className)}
                data-has-drag-indicator={hasDragIndicator}
                style={{ zIndex: surfaceZIndex, ...style }}
                {...rest}
            >
                {hasDragIndicator && <BottomDrawer.DragIndicator />}
                {children}
            </Drawer.Content>
        </Drawer.Portal>
    )
}

function Close(props: ComponentPropsWithoutRef<typeof Drawer.Close>) {
    const { children, ...rest } = props
    const { isDismissable, setIsOpen } = useContext(BottomDrawerContext)

    const handleClick = () => {
        if (!isDismissable) {
            setIsOpen(false)
        }
    }

    return (
        <Drawer.Close {...rest} onClickCapture={handleClick}>
            {children}
        </Drawer.Close>
    )
}

export default {
    Root,
    Trigger,
    Content,
    Close,
    DragIndicator: DrawerDragIndicator,
}
